// frontend/src/components/PerformanceDashboard.jsx
// ✅ FIXED: API path now includes /creator prefix
import React from "react";
import { getIdentityHeaders, waitForIdentity } from '../lib/identityGoogle';
import { API_BASE } from "../lib/api";

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

export default function PerformanceDashboard() {
  const [loading, setLoading] = React.useState(true);
  const [metrics, setMetrics] = React.useState([]);
  const [period, setPeriod] = React.useState({ days: 30, startDate: null, endDate: null });
  const [selectedDays, setSelectedDays] = React.useState(30);

  React.useEffect(() => {
    loadMetrics();
  }, [selectedDays]);

  async function loadMetrics() {
    setLoading(true);
    try {
      await waitForIdentity();

      // ✅ FIXED: Added /creator prefix to match router mount point
      const res = await fetch(
        api(`/api/creator/analytics/my-styles/performance?days=${selectedDays}`),
        {
          credentials: "include",
          headers: getIdentityHeaders(),
        }
      );

      if (!res.ok) throw new Error("Failed to load metrics");

      const data = await res.json();
      setMetrics(data.metrics || []);
      setPeriod(data.period || { days: selectedDays, startDate: null, endDate: null });
    } catch (err) {
      console.error("[PerformanceDashboard] Error:", err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="perf-loading">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (metrics.length === 0) {
    return (
      <div className="perf-empty">
        <p>No performance data available yet. Create some styles and they'll appear here!</p>
      </div>
    );
  }

  const maxUses = Math.max(...metrics.map(m => m.totalUses), 1);

  return (
    <div className="perf-dashboard">
      <div className="perf-header">
        <h2>Styles Performance</h2>
        <select
          className="perf-time-filter"
          value={selectedDays}
          onChange={(e) => setSelectedDays(Number(e.target.value))}
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      <div className="perf-grid">
        {metrics.map((metric, index) => (
          <StyleCard
            key={metric.styleId}
            metric={metric}
            rank={index + 1}
            maxUses={maxUses}
          />
        ))}
      </div>
    </div>
  );
}

function StyleCard({ metric, rank, maxUses }) {
  const getRankEmoji = () => {
    if (rank === 1) return "1st";
    if (rank === 2) return "2nd";
    if (rank === 3) return "3rd";
    return `#${rank}`;
  };

  const getTrendEmoji = () => {
    if (metric.trend === "up") return "↗";
    if (metric.trend === "down") return "↘";
    return "→";
  };

  const getTrendText = () => {
    const change = metric.usesChange;
    if (change > 0) return `+${change}%`;
    if (change < 0) return `${change}%`;
    return "No change";
  };

  const getQualityColor = () => {
    if (metric.qualityScore >= 80) return "#10B981"; // Green
    if (metric.qualityScore >= 60) return "#F59E0B"; // Yellow
    return "#E9E3FF"; // Purplish-grey like Royalty Program button
  };

  const getQualityLabel = () => {
    if (metric.qualityScore >= 80) return "Excellent";
    if (metric.qualityScore >= 60) return "Good";
    return "Needs Work";
  };

  const barWidth = (metric.totalUses / maxUses) * 100;

  return (
    <div className="perf-card">
      {/* Header */}
      <div className="perf-card-header">
        <div className="perf-card-title">
          <span className="perf-rank">{getRankEmoji()}</span>
          <h3>{metric.styleName}</h3>
        </div>
        <div className="perf-trend">
          <span className="trend-emoji">{getTrendEmoji()}</span>
          <span className="trend-text">{getTrendText()}</span>
        </div>
      </div>

      {/* Usage Bar */}
      <div className="perf-bar-container">
        <div
          className={`perf-bar ${metric.qualityScore < 60 ? 'perf-bar-grey' : ''}`}
          style={{
            width: `${barWidth}%`,
            background: getQualityColor(),
          }}
        ></div>
        <span className="perf-bar-label">{metric.totalUses} uses</span>
      </div>

      {/* Conversion Rate */}
      <div className="perf-conversion">
        <span className="conversion-label">
          {metric.totalViews.toLocaleString()} views → {metric.totalUses} uses
        </span>
        <span className="conversion-rate">
          {metric.conversionRate}% conversion
        </span>
      </div>

      {/* Sparkline - Enhanced to match earnings analytics */}
      <div className="perf-sparkline">
        <Sparkline 
          data={metric.sparklineData || generateSparklineData(metric.totalUses, rank - 1)} 
          styleId={metric.styleId} 
        />
      </div>

      {/* Metrics Row */}
      <div className="perf-metrics">
        <div className="metric">
          <span className="metric-label">Score</span>
          <span
            className="metric-value"
            style={{ color: getQualityColor() }}
          >
            {metric.qualityScore}
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Likes</span>
          <span className="metric-value">{metric.likes}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Reports</span>
          <span className="metric-value">{metric.reports}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="perf-card-footer">
        {metric.lastUsed ? (
          <span className="last-used">
            Last used: {formatTimeAgo(metric.lastUsed)}
          </span>
        ) : (
          <span className="last-used">Never used</span>
        )}
        <span
          className={`quality-badge ${metric.qualityScore < 60 ? 'quality-badge-grey' : ''}`}
          style={{ background: getQualityColor() }}
        >
          {getQualityLabel()}
        </span>
      </div>
    </div>
  );
}

function Sparkline({ data, styleId = 'default' }) {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const width = 100;
  const height = 40;
  const padding = 4;
  
  // Calculate points
  const points = data.map((value, index) => {
    const x = (index / Math.max(data.length - 1, 1)) * width;
    const normalizedValue = (value - min) / range;
    const y = height - padding - (normalizedValue * (height - padding * 2));
    return { x, y, value };
  });

  // Create smooth curve using bezier curves
  let pathD = `M ${points[0].x},${points[0].y}`;
  
  for (let i = 1; i < points.length; i++) {
    const curr = points[i];
    const prev = points[i - 1];
    
    // Control points for smooth bezier curve
    const cpX1 = prev.x + (curr.x - prev.x) / 3;
    const cpY1 = prev.y;
    const cpX2 = prev.x + 2 * (curr.x - prev.x) / 3;
    const cpY2 = curr.y;
    
    pathD += ` C ${cpX1},${cpY1} ${cpX2},${cpY2} ${curr.x},${curr.y}`;
  }

  // Create area fill path
  const areaPathD = pathD + 
    ` L ${points[points.length - 1].x},${height}` +
    ` L ${points[0].x},${height} Z`;

  // Use styleId for unique gradient IDs (perf prefix to avoid conflicts)
  const gradientId = `perfSparklineGradient-${styleId}`;
  const areaGradientId = `perfSparklineAreaGradient-${styleId}`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="sparkline-svg"
      preserveAspectRatio="none"
    >
      <defs>
        {/* Line gradient */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: "#6D5EF9", stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: "#A78BFA", stopOpacity: 1 }} />
        </linearGradient>
        
        {/* Area gradient */}
        <linearGradient id={areaGradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#6D5EF9", stopOpacity: 0.2 }} />
          <stop offset="100%" style={{ stopColor: "#6D5EF9", stopOpacity: 0.02 }} />
        </linearGradient>
      </defs>
      
      {/* Area fill */}
      <path
        d={areaPathD}
        fill={`url(#${areaGradientId})`}
      />
      
      {/* Line stroke */}
      <path
        d={pathD}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function generateSparklineData(uses, styleIndex) {
  // Generate realistic sparkline data based on usage
  const points = Math.max(7, Math.min(uses + 3, 20));
  const data = [];
  
  // Create a trend pattern - different for each style
  const trendBase = styleIndex === 0 ? 0.8 : styleIndex === 1 ? 0.5 : 0.3;
  
  for (let i = 0; i < points; i++) {
    const progress = i / (points - 1);
    
    // Base trend (upward for top styles, flat for others)
    let value = trendBase * (1 + progress * 0.3);
    
    // Add wave pattern
    value += Math.sin(i * 0.8) * 0.15;
    
    // Add some randomness
    value += (Math.random() - 0.5) * 0.1;
    
    // Keep values positive and normalized
    data.push(Math.max(0.1, value));
  }
  
  return data;
}

function formatTimeAgo(date) {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "1d ago";
  return `${diffDays}d ago`;
}

// Styles embedded in the component
const styles = `
.perf-dashboard{
  width:100%;
}

.perf-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:24px;
}

.perf-header h2{
  font-size:24px;
  font-weight:800;
  color:#111827;
  margin:0;
}

.perf-time-filter{
  padding:8px 16px;
  border:1px solid #E5E7EB;
  border-radius:8px;
  font-size:14px;
  font-weight:600;
  background:#fff;
  cursor:pointer;
  transition: all 0.2s;
}

.perf-time-filter:hover{
  border-color:#6D5EF9;
  box-shadow:0 0 0 3px #F3F0FF;
}

.perf-grid{
  display:grid;
  gap:16px;
}

.perf-card{
  background:#fff;
  border:1px solid #E5E7EB;
  border-radius:12px;
  padding:20px;
  transition: all 0.2s;
}

.perf-card:hover{
  box-shadow:0 4px 12px rgba(0,0,0,0.08);
  transform:translateY(-2px);
}

.perf-card-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:12px;
}

.perf-card-title{
  display:flex;
  align-items:center;
  gap:8px;
}

.perf-rank{
  font-size:14px;
  font-weight:800;
  color:#6D5EF9;
  background:#F3F0FF;
  padding:4px 8px;
  border-radius:6px;
}

.perf-card-title h3{
  font-size:18px;
  font-weight:700;
  color:#111827;
  margin:0;
}

.perf-trend{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:14px;
}

.trend-emoji{
  font-size:16px;
}

.trend-text{
  font-weight:700;
  color:#6B7280;
}

.perf-bar-container{
  position:relative;
  height:32px;
  background:#F3F4F6;
  border-radius:8px;
  overflow:hidden;
  margin-bottom:12px;
}

.perf-bar{
  height:100%;
  border-radius:8px;
  transition: width 0.6s ease;
}

.perf-bar-grey{
  background:#F3F0FF !important;
  border:1px solid #E7E2FF;
}

.perf-bar-grey + .perf-bar-label{
  color:#6D5EF9;
  text-shadow:none;
}

.perf-bar-label{
  position:absolute;
  left:12px;
  top:50%;
  transform:translateY(-50%);
  font-size:14px;
  font-weight:700;
  color:#fff;
  text-shadow:0 1px 2px rgba(0,0,0,0.3);
}

.perf-conversion{
  display:flex;
  justify-content:space-between;
  align-items:center;
  font-size:13px;
  margin-bottom:12px;
  padding:8px 0;
  border-bottom:1px solid #F3F4F6;
}

.conversion-label{
  color:#6B7280;
}

.conversion-rate{
  font-weight:700;
  color:#111827;
}

.perf-sparkline{
  margin-top:12px;
  margin-bottom:8px;
  padding:12px 0;
  border-top:1px solid #F3F4F6;
}

.sparkline-svg{
  width:100%;
  height:50px;
  display:block;
}

.perf-metrics{
  display:grid;
  grid-template-columns:repeat(3, 1fr);
  gap:12px;
  margin-bottom:16px;
  padding:16px 0;
  border-top:1px solid #F3F4F6;
  border-bottom:1px solid #F3F4F6;
}

.metric{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:4px;
}

.metric-label{
  font-size:11px;
  color:#9CA3AF;
  text-transform:uppercase;
  font-weight:600;
}

.metric-value{
  font-size:16px;
  font-weight:800;
  color:#111827;
}

.perf-card-footer{
  display:flex;
  justify-content:space-between;
  align-items:center;
}

.last-used{
  font-size:12px;
  color:#6B7280;
}

.quality-badge{
  padding:4px 12px;
  border-radius:999px;
  font-size:11px;
  font-weight:800;
  color:#fff;
  text-transform:uppercase;
}

.quality-badge-grey{
  background:#F3F0FF !important;
  color:#6D5EF9 !important;
  border:1px solid #E7E2FF;
}

.perf-loading, .perf-empty{
  text-align:center;
  padding:48px 20px;
  color:#6B7280;
}

.spinner{
  width:40px;
  height:40px;
  border:4px solid #F3F4F6;
  border-top-color:#6D5EF9;
  border-radius:50%;
  animation:spin 0.8s linear infinite;
  margin:0 auto 16px;
}

@keyframes spin{
  to{ transform:rotate(360deg); }
}

@media (max-width:768px){
  .perf-header{
    flex-direction:column;
    gap:12px;
    align-items:flex-start;
  }
  
  .perf-time-filter{
    width:100%;
  }
}
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}